"use client";

import { useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export interface ProgressSliderProps {
    /** Current step index (0-based) */
    currentIndex: number;
    /** Total number of steps (e.g. number of cards) */
    totalCount: number;
    /** If set, bar moves in this many steps (e.g. n-2). Otherwise uses totalCount - 1. */
    stepsCount?: number;
    /** Width in px of the movable bar (default 61) */
    barWidthPx?: number;
    /** Optional class for the track container */
    trackClassName?: string;
    /** Optional class for the moving bar */
    barClassName?: string;
    /** Optional ref to attach to the track (e.g. for parent layout) */
    trackRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 * Horizontal progress slider: a track with a moving bar that indicates current position.
 * Bar position = (currentIndex / (totalCount - 1)) * (trackWidth - barWidth) when totalCount > 1.
 */
export function ProgressSlider({
    currentIndex,
    totalCount,
    stepsCount: stepsCountProp,
    barWidthPx = 61,
    trackClassName,
    barClassName,
    trackRef: externalTrackRef,
}: ProgressSliderProps) {
    const internalTrackRef = useRef<HTMLDivElement>(null);
    const [trackWidth, setTrackWidth] = useState(0);

    const setRef = (el: HTMLDivElement | null) => {
        (internalTrackRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        if (externalTrackRef) (externalTrackRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    };

    useLayoutEffect(() => {
        const el = internalTrackRef.current;
        if (!el) return;
        const update = () => setTrackWidth(el.offsetWidth);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const stepsCount = stepsCountProp ?? Math.max(0, totalCount - 1);
    const progressPercentage =
        stepsCount > 0 ? Math.min(100, (currentIndex / stepsCount) * 100) : 0;
    const maxBarOffset = Math.max(0, trackWidth - barWidthPx);
    const barOffsetPx = Math.min(
        (progressPercentage / 100) * maxBarOffset,
        maxBarOffset
    );

    return (
        <div
            ref={setRef}
            className={cn(
                "relative h-[3px] bg-gray-300 rounded-full shrink-0 overflow-visible",
                trackClassName
            )}
        >
            <div
                className={cn(
                    "absolute top-0 left-0 h-full bg-gray-800 rounded-full transition-[transform] duration-300 ease-in-out",
                    barClassName
                )}
                style={{
                    width: barWidthPx,
                    transform: `translateX(${barOffsetPx}px)`,
                }}
            />
        </div>
    );
}
